import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

import { PropertyService } from '../../services/property.service';
import { OrganizationService } from '../../services/organization.service';
import { AuthService } from '../../services/auth.service';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';

@Component({
  selector: 'app-marketing-landing',
  templateUrl: './marketing-landing.component.html',
  styleUrls: ['./marketing-landing.component.scss']
})
export class MarketingLandingComponent implements OnInit {
  organization;
  attributes;
  availableProperties: any[] = [];

  loading: boolean = true;
  private dataLoaded = false;
  private imageLoaded = false;
    
  constructor(private propertyService: PropertyService,
              private organizationService: OrganizationService,
              private authService: AuthService,
              public router: Router) { }

  ngOnInit() {
    this.organizationService.getOrganization().then((organization) => {
      this.organization = organization;
      if (!this.organization || !this.organization.marketingImageUrl) {
        this.imageLoaded = true;
      }
    }).catch(() => {
      this.organization = null;
      this.imageLoaded = true;
    });

    this.propertyService.getAttributes().then((attributes) => {
      this.attributes = attributes;
      this.dataLoaded = true;
      this.maybeCheckLoadingComplete();
    });

    // Load and filter properties
    this.loadAvailableProperties();
  }

  async loadAvailableProperties() {
    try {
      const allProperties = await this.propertyService.getPublicProperties();
      if (!allProperties || !Array.isArray(allProperties)) {
        this.availableProperties = [];
        return;
      }

      // Filter properties: only show those without active leases
      // The public properties endpoint already includes lease information in the leases array
      this.availableProperties = allProperties.filter((property) => {
        // Check if property has an active lease in the leases array
        if (property.leases && Array.isArray(property.leases)) {
          const hasActiveLease = property.leases.some(lease => 
            lease.status === 'ACTIVE' || lease.status === 'Active'
          );
          if (hasActiveLease) {
            return false; // Don't show properties with active leases
          }
        }

        // Show all properties that don't have active leases
        // (The status field is secondary - we primarily rely on lease status)
        return true;
      });
    } catch (error) {
      console.error('Error loading properties:', error);
      this.availableProperties = [];
    }
  }

  getProperties() {
    return this.availableProperties;
  }

  getImageUrl(path: string) {
    if (!path || !path.trim()) {
      return '';
    }

    // Data URIs and non-S3 http URLs: use as-is
    if (path.startsWith('data:') || (path.startsWith('http') && !path.includes('amazonaws.com'))) {
      return path;
    }

    // S3 URLs: convert to API proxy path so we serve via backend
    if (path.startsWith('http') && path.includes('amazonaws.com')) {
      const fileName = path.substring(path.lastIndexOf('/') + 1);
      return environment.apiBaseUrl + `/v1/public/organization/image/${fileName}`;
    }

    // Relative path (e.g. /v1/public/organization/image/marketing.jpg) or filename only
    if (path.startsWith('/')) {
      return environment.apiBaseUrl + path;
    }
    return environment.apiBaseUrl + `/v1/public/organization/image/${path}`;
  }

  /** Placeholder when no marketing image: request API placeholder so img always loads */
  getPlaceholderImageUrl(): string {
    return environment.apiBaseUrl + '/v1/public/organization/image/placeholder.png';
  }

  onImageLoad() {
    this.imageLoaded = true;
    this.maybeCheckLoadingComplete();
  }

  onImageError() {
    this.imageLoaded = true;
    this.maybeCheckLoadingComplete();
  }

  private maybeCheckLoadingComplete() {
    if (this.dataLoaded && this.imageLoaded) {
      this.loading = false;
    }
  }
}
